'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Calendar,
  Mail,
} from 'lucide-react';
import { trackLeadFormSubmission, trackScheduleMeetingClick, trackFormStart } from '@/lib/analytics';

/* ------------------------------------------------------------------ */
/* Assessment model                                                    */
/* ------------------------------------------------------------------ */

interface Choice {
  label: string;
  /** 0 = lowest risk, higher = more exposure */
  risk: number;
  /** Raw value carried forward to the capture form (platform / employees). */
  value?: string;
}

interface Question {
  id: string;
  text: string;
  /** Short label used when summarizing answers in the lead notification. */
  summary: string;
  choices: Choice[];
}

const QUESTIONS: Question[] = [
  {
    id: 'platform',
    summary: 'Email platform',
    text: 'Which email platform does your team run on?',
    choices: [
      { label: 'Microsoft 365 / Exchange Online', risk: 1, value: 'Microsoft 365' },
      { label: 'Google Workspace / Gmail', risk: 1, value: 'Google Workspace' },
      { label: 'On-prem or hybrid Exchange', risk: 2, value: 'Exchange (on-prem/hybrid)' },
      { label: 'Something else', risk: 1, value: 'Other' },
    ],
  },
  {
    id: 'protection',
    summary: 'Current protection',
    text: 'What is protecting your inbox today, beyond the basics?',
    choices: [
      { label: 'Just the native filtering that comes built in', risk: 3 },
      { label: 'Microsoft E5 / Defender for Office 365 add-ons', risk: 2 },
      { label: 'A dedicated third-party email security tool', risk: 0 },
      { label: 'Not sure what we have', risk: 3 },
    ],
  },
  {
    id: 'incident',
    summary: 'Recent phishing/BEC incident',
    text: 'In the last 12 months, has anyone been hit by phishing or business email compromise (BEC)?',
    choices: [
      { label: 'Yes — a BEC, wire-fraud, or account-takeover incident', risk: 3 },
      { label: 'Yes — someone clicked a phishing email / entered credentials', risk: 2 },
      { label: 'Not that we know of', risk: 1 },
      { label: 'No — and we would have caught it', risk: 0 },
    ],
  },
  {
    id: 'dmarc',
    summary: 'DMARC enforcement',
    text: 'Is DMARC enforced (p=reject) so attackers cannot spoof your domain?',
    choices: [
      { label: 'No DMARC record at all', risk: 3 },
      { label: 'We have DMARC, but only at p=none (monitoring)', risk: 2 },
      { label: 'Not sure', risk: 2 },
      { label: 'Yes — enforced at p=reject', risk: 0 },
    ],
  },
  {
    id: 'training',
    summary: 'Awareness training / phishing sims',
    text: 'Do you run security-awareness training and phishing simulations?',
    choices: [
      { label: 'No, none', risk: 3 },
      { label: 'Once a year or ad hoc', risk: 2 },
      { label: 'Occasionally, but no phishing simulations', risk: 1 },
      { label: 'Yes — regular training plus simulated phishing', risk: 0 },
    ],
  },
  {
    id: 'regulated',
    summary: 'Regulated / PHI data',
    text: 'Do you handle regulated or PHI data (HIPAA, finance, legal)?',
    choices: [
      { label: 'Yes — PHI / HIPAA-regulated data', risk: 2 },
      { label: 'Yes — other regulated or sensitive data', risk: 2 },
      { label: 'Some sensitive data, not formally regulated', risk: 1 },
      { label: 'No regulated data', risk: 0 },
    ],
  },
  {
    id: 'employees',
    summary: 'Employee count',
    text: 'How many employees use email at your organization?',
    choices: [
      { label: '1–10', risk: 0, value: '1-10' },
      { label: '11–50', risk: 1, value: '11-50' },
      { label: '51–200', risk: 1, value: '51-200' },
      { label: '201–500', risk: 2, value: '201-500' },
      { label: '500+', risk: 2, value: '500+' },
    ],
  },
];

const MAX_SCORE = QUESTIONS.reduce(
  (sum, q) => sum + Math.max(...q.choices.map((c) => c.risk)),
  0
);

/* ------------------------------------------------------------------ */
/* Risk tiers                                                          */
/* ------------------------------------------------------------------ */

interface Tier {
  key: 'high' | 'elevated' | 'moderate' | 'low';
  name: string;
  icon: typeof Shield;
  tagline: string;
  /** Header gradient/accent for the result card badge. */
  badgeClass: string;
  exposed: string;
}

const TIERS: Tier[] = [
  {
    key: 'high',
    name: 'High Risk',
    icon: ShieldAlert,
    tagline: 'Multiple open gaps attackers actively target',
    badgeClass: 'bg-red-500/20 text-red-50 ring-1 ring-red-200/40',
    exposed:
      'Your inbox is leaning on filtering that targeted attacks are designed to slip through. Check Point reports 18.8% of phishing bypasses Microsoft Defender, and 90%+ of attacks start with a malicious email. With the gaps below open, a single convincing BEC or credential-phish email can reach a user before anything stops it.',
  },
  {
    key: 'elevated',
    name: 'Elevated Risk',
    icon: ShieldAlert,
    tagline: 'Real gaps in your last line of defense',
    badgeClass: 'bg-orange-500/20 text-orange-50 ring-1 ring-orange-200/40',
    exposed:
      'You have some controls, but meaningful gaps remain. Native filtering catches commodity spam — it is the targeted phishing, impersonation, and account-takeover attempts (the 18.8% Check Point measures bypassing Defender) that land in the inbox. The gaps below are the most likely entry points.',
  },
  {
    key: 'moderate',
    name: 'Moderate Risk',
    icon: Shield,
    tagline: 'A solid base with a few exposures to close',
    badgeClass: 'bg-amber-400/20 text-amber-50 ring-1 ring-amber-200/40',
    exposed:
      'You are in reasonable shape, but a few gaps still leave room for a targeted email to get through. Even strong native setups miss the BEC and impersonation attacks built to evade them — closing the items below removes the easiest paths in.',
  },
  {
    key: 'low',
    name: 'Low Risk',
    icon: ShieldCheck,
    tagline: 'Strong posture — worth verifying what still slips through',
    badgeClass: 'bg-emerald-500/20 text-emerald-50 ring-1 ring-emerald-200/40',
    exposed:
      'Your posture is strong on paper. Even so, no native filter is the last line of defense — Check Point still measures 18.8% of phishing bypassing Microsoft Defender. A retroactive mailbox scan is the only way to confirm nothing targeted is already sitting in your inboxes.',
  },
];

function tierForScore(total: number, max: number): Tier {
  const pct = max > 0 ? total / max : 0;
  if (pct >= 0.6) return TIERS[0];
  if (pct >= 0.4) return TIERS[1];
  if (pct >= 0.2) return TIERS[2];
  return TIERS[3];
}

/* ------------------------------------------------------------------ */
/* Gap detection                                                       */
/* ------------------------------------------------------------------ */

interface Gap {
  title: string;
  detail: string;
}

function deriveGaps(risk: Record<string, number>): Gap[] {
  const gaps: Gap[] = [];

  if ((risk.protection ?? 0) >= 3) {
    gaps.push({
      title: 'Relying on native filtering alone',
      detail:
        'Check Point measures 18.8% of phishing bypassing Microsoft Defender. Without a dedicated layer behind your native filter, those land in the inbox.',
    });
  } else if ((risk.protection ?? 0) === 2) {
    gaps.push({
      title: 'No dedicated email security layer behind your native filter',
      detail:
        'E5 / Defender add-ons help, but targeted BEC and impersonation attacks are built to evade them — a purpose-built last line of defense catches what they miss.',
    });
  }

  if ((risk.dmarc ?? 0) >= 2) {
    gaps.push({
      title: 'DMARC is not enforced (p=reject)',
      detail:
        'Without enforced DMARC, attackers can spoof your own domain to staff, customers, and vendors — a primary vector for BEC and wire fraud.',
    });
  }

  if ((risk.training ?? 0) >= 2) {
    gaps.push({
      title: 'Little or no security-awareness training',
      detail:
        'When a phishing email does reach a user, training and simulations are what keep them from clicking. Without them, every bypass is a live risk.',
    });
  }

  if ((risk.incident ?? 0) >= 2) {
    gaps.push({
      title: 'A phishing or BEC incident in the last 12 months',
      detail:
        'A prior incident is the strongest signal that current controls are being tested and beaten. Attackers often return to a proven target.',
    });
  }

  if ((risk.regulated ?? 0) >= 2) {
    gaps.push({
      title: 'Regulated / PHI data raises the stakes',
      detail:
        'A successful email compromise becomes a reportable breach. Managed email security with DLP and HIPAA / SOC 2 / ISO 27001 certification reduces that exposure.',
    });
  }

  if ((risk.platform ?? 0) >= 2) {
    gaps.push({
      title: 'On-prem / hybrid Exchange surface',
      detail:
        'Hybrid and on-prem Exchange add patching and exposure that cloud-native, API-based protection sits in front of without an MX change.',
    });
  }

  return gaps;
}

/* ------------------------------------------------------------------ */
/* Capture form options                                                */
/* ------------------------------------------------------------------ */

const PLATFORM_OPTIONS = [
  'Microsoft 365',
  'Google Workspace',
  'Exchange (on-prem/hybrid)',
  'Other',
];

const EMPLOYEE_OPTIONS = [
  { value: '1-10', label: '1–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-500', label: '201–500' },
  { value: '500+', label: '500+' },
];

type Phase = 'quiz' | 'result' | 'success';
type SubmitStatus = 'idle' | 'submitting' | 'error';

export default function RiskScoreClient() {
  const [phase, setPhase] = useState<Phase>('quiz');
  const [step, setStep] = useState(0);
  /** Maps question id -> selected choice INDEX (not risk, so duplicate-risk choices stay distinct). */
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [formStarted, setFormStarted] = useState(false);

  // Capture fields (platform + employees pre-filled from the quiz)
  const [email, setEmail] = useState('');
  const [employees, setEmployees] = useState('');
  const [platform, setPlatform] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [errors, setErrors] = useState<{ email?: string; employees?: string; platform?: string }>({});
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');

  const [loadTime] = useState(() => Date.now());

  /** Per-question risk values, derived from the selected choice index. */
  const riskMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const q of QUESTIONS) {
      const idx = answers[q.id];
      if (idx !== undefined && q.choices[idx]) map[q.id] = q.choices[idx].risk;
    }
    return map;
  }, [answers]);

  const totalScore = useMemo(
    () => QUESTIONS.reduce((sum, q) => sum + (riskMap[q.id] ?? 0), 0),
    [riskMap]
  );
  const answeredCount = Object.keys(answers).length;
  const tier = useMemo(() => tierForScore(totalScore, MAX_SCORE), [totalScore]);
  const gaps = useMemo(() => deriveGaps(riskMap), [riskMap]);

  const startTracking = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Email Security Risk Score', 'email_security_risk_score');
    }
  };

  const handleAnswer = (question: Question, choiceIndex: number) => {
    startTracking();
    const choice = question.choices[choiceIndex];
    setAnswers((prev) => ({ ...prev, [question.id]: choiceIndex }));

    // Carry platform / employee selections forward to the capture form.
    if (question.id === 'platform' && choice.value) setPlatform(choice.value);
    if (question.id === 'employees' && choice.value) setEmployees(choice.value);

    if (step < QUESTIONS.length - 1) {
      setStep((s) => s + 1);
    } else {
      setPhase('result');
    }
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!email.trim()) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address';
    if (!employees.trim()) next.employees = 'Please select your team size';
    if (!platform.trim()) next.platform = 'Please select your email platform';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildMessage = (): string => {
    const answerLines = QUESTIONS.map((q) => {
      const idx = answers[q.id];
      const choice = idx !== undefined ? q.choices[idx] : undefined;
      return `- ${q.summary}: ${choice ? choice.label : 'No answer'}`;
    }).join('\n');

    const gapLines = gaps.length
      ? gaps.map((g) => `- ${g.title}`).join('\n')
      : '- No major gaps flagged';

    return [
      `[Topic: Email Security Risk Score]`,
      ``,
      `Risk level: ${tier.name} (score ${totalScore}/${MAX_SCORE})`,
      `Email platform: ${platform}`,
      `Number of employees: ${employees}`,
      ``,
      `Gaps found:`,
      gapLines,
      ``,
      `Assessment answers:`,
      answerLines,
    ].join('\n');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot — silently pretend success for bots.
    if (honeypot) {
      setPhase('success');
      return;
    }
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');
    setEmailSuggestion('');

    const nameParts = name.trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || 'Email Security';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Lead';

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.trim(),
          company: company.trim(),
          message: buildMessage(),
          formName: 'Email Security Risk Score',
          src: 'email-security',
          cat: 'security',
          id: `risk-${tier.key}`,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          _t: loadTime,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (typeof data.suggestion === 'string' && data.suggestion) {
          setEmailSuggestion(data.suggestion);
        }
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }

      trackLeadFormSubmission(
        'Email Security Risk Score',
        'email_security_risk_score',
        undefined,
        email.trim()
      );

      setPhase('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const applySuggestion = () => {
    setEmail(emailSuggestion);
    setEmailSuggestion('');
    setErrors((e) => ({ ...e, email: undefined }));
    setStatus('idle');
  };

  /* ---------------------------------------------------------------- */
  /* SUCCESS                                                           */
  /* ---------------------------------------------------------------- */
  if (phase === 'success') {
    const TierIcon = tier.icon;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 md:p-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full mb-6">
          <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          Your Security Checkup is on the way.
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
          We&apos;ve received your assessment. A CISSP-led email security expert will reach out to
          set up your free 14-day Security Checkup &mdash; it deploys in 5 minutes via API with no MX
          change and retroactively scans your existing mailboxes for what&apos;s already slipped past
          your <span className="font-semibold text-gray-800 dark:text-gray-200">{platform || 'current'}</span> filter.
        </p>

        <div className="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl mb-8">
          <TierIcon className="w-6 h-6 text-[#2813e8]" />
          <span className="text-[#2813e8] font-semibold">Your result: {tier.name}</span>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 dark:border-blue-900 rounded-xl p-6 mb-6 max-w-xl mx-auto">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Want to get started right now? Book your checkup kickoff.
          </p>
          <a
            href="https://hs.inventivehq.com/meetings/sean1324/contactpage"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackScheduleMeetingClick('email_security_risk_success')}
            className="inline-flex items-center justify-center gap-2 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            <Calendar className="w-5 h-5" />
            Schedule a Call Now
          </a>
        </div>

        <Link
          href="/services/email-security"
          className="text-[#2813e8] hover:text-[#2cc3d1] font-medium transition-colors"
        >
          Explore managed email security &rarr;
        </Link>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* RESULT + CAPTURE                                                  */
  /* ---------------------------------------------------------------- */
  if (phase === 'result') {
    const TierIcon = tier.icon;
    return (
      <div className="space-y-8">
        {/* Result card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] text-white p-8 md:p-10">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-3">
              <CheckCircle2 className="w-4 h-4" />
              Assessment complete
            </div>
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-16 h-16 bg-white/15 rounded-2xl flex items-center justify-center">
                <TierIcon className="w-9 h-9" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider text-white/80 mb-1">Your email security risk</p>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-3xl md:text-4xl font-bold">{tier.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tier.badgeClass}`}>
                    {totalScore}/{MAX_SCORE}
                  </span>
                </div>
                <p className="text-white/90">{tier.tagline}</p>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-10 space-y-6">
            <div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                Here&apos;s what&apos;s exposed
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{tier.exposed}</p>
            </div>

            {gaps.length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">
                  Gaps we found ({gaps.length})
                </h4>
                <ul className="space-y-3">
                  {gaps.map((g) => (
                    <li
                      key={g.title}
                      className="flex items-start gap-3 p-4 rounded-xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20"
                    >
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{g.title}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{g.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 dark:border-blue-900 rounded-xl p-6">
              <h4 className="text-lg font-bold text-[#2813e8] mb-2">Your recommended next step</h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                See exactly what&apos;s getting past your current filter &mdash; with a free 14-day
                Security Checkup. It deploys in 5 minutes via API with no MX change, sits behind your
                native filtering as the last line of defense, and retroactively scans your existing
                mailboxes so you can see real threats already sitting in inboxes. No commitment.
              </p>
            </div>
          </div>
        </div>

        {/* Lead capture */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 md:p-10">
          <div className="mb-6">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Start your free 14-day Security Checkup
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Tell us where to reach you and a CISSP-led email security expert will set up your
              checkup &mdash; deploys in 5 minutes, no MX change, runs a retroactive scan of your
              existing mailboxes. No obligation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" name="Email Security Risk Score">
            {/* Honeypot */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="esr-website">Website</label>
              <input
                type="text"
                id="esr-website"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Email (required) */}
            <div>
              <label htmlFor="esr-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Work email<span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                type="email"
                id="esr-email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((er) => ({ ...er, email: undefined }));
                  if (emailSuggestion) setEmailSuggestion('');
                }}
                onFocus={startTracking}
                aria-invalid={!!errors.email}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.email
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
                } bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 transition-colors`}
                placeholder="you@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Number of employees (required) */}
              <div>
                <label htmlFor="esr-employees" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Number of employees<span className="text-red-600 dark:text-red-400">*</span>
                </label>
                <select
                  id="esr-employees"
                  name="employees"
                  value={employees}
                  onChange={(e) => {
                    setEmployees(e.target.value);
                    if (errors.employees) setErrors((er) => ({ ...er, employees: undefined }));
                  }}
                  onFocus={startTracking}
                  aria-invalid={!!errors.employees}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.employees
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
                  } bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 transition-colors ${
                    employees ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <option value="">Select team size...</option>
                  {EMPLOYEE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.employees && (
                  <p className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.employees}
                  </p>
                )}
              </div>

              {/* Email platform (required) */}
              <div>
                <label htmlFor="esr-platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email platform<span className="text-red-600 dark:text-red-400">*</span>
                </label>
                <select
                  id="esr-platform"
                  name="platform"
                  value={platform}
                  onChange={(e) => {
                    setPlatform(e.target.value);
                    if (errors.platform) setErrors((er) => ({ ...er, platform: undefined }));
                  }}
                  onFocus={startTracking}
                  aria-invalid={!!errors.platform}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.platform
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
                  } bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 transition-colors ${
                    platform ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <option value="">Select platform...</option>
                  {PLATFORM_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                {errors.platform && (
                  <p className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.platform}
                  </p>
                )}
              </div>
            </div>

            {/* Optional: name + company */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="esr-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="esr-name"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={startTracking}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-[#2813e8] focus:border-[#2813e8] focus:outline-none focus:ring-2 transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="esr-company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="esr-company"
                  name="company"
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  onFocus={startTracking}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-[#2813e8] focus:border-[#2813e8] focus:outline-none focus:ring-2 transition-colors"
                  placeholder="Acme Inc."
                />
              </div>
            </div>

            {/* Error */}
            {status === 'error' && (
              <div role="alert" className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 dark:text-red-300 font-medium">Could not submit</p>
                  <p className="text-red-700 dark:text-red-300 text-sm">{errorMessage}</p>
                  {emailSuggestion && (
                    <p className="mt-2 text-sm text-red-700 dark:text-red-300">
                      Did you mean{' '}
                      <button
                        type="button"
                        onClick={applySuggestion}
                        className="font-semibold text-[#2813e8] underline hover:text-[#2cc3d1] transition-colors"
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </p>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full px-8 py-4 bg-[#2813e8] hover:bg-[#1f0fb8] disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Start My Free Security Checkup
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              We respect your privacy. No spam, ever. View our{' '}
              <Link href="/privacy-policy" className="underline hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                Privacy Policy
              </Link>
              .
            </p>
          </form>

          <button
            onClick={() => {
              setPhase('quiz');
              setStep(QUESTIONS.length - 1);
            }}
            className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-[#2813e8] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to questions
          </button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* QUIZ                                                              */
  /* ---------------------------------------------------------------- */
  const question = QUESTIONS[step];
  const currentAnswer = answers[question.id];
  const progressPct = Math.round((answeredCount / QUESTIONS.length) * 100);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 md:p-10">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
          <span className="font-semibold text-[#2813e8]">
            Question {step + 1} of {QUESTIONS.length}
          </span>
          <span>{progressPct}% complete</span>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {question.text}
      </h3>

      <div className="space-y-3">
        {question.choices.map((choice, choiceIndex) => {
          const selected = currentAnswer === choiceIndex;
          return (
            <button
              key={choice.label}
              type="button"
              onClick={() => handleAnswer(question, choiceIndex)}
              className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center gap-4 group ${
                selected
                  ? 'border-[#2813e8] bg-blue-50 dark:bg-blue-950/30 ring-2 ring-[#2813e8]/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#2cc3d1] hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <span
                className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  selected
                    ? 'border-[#2813e8] bg-[#2813e8]'
                    : 'border-gray-300 dark:border-gray-600 group-hover:border-[#2cc3d1]'
                }`}
              >
                {selected && <CheckCircle2 className="w-4 h-4 text-white" />}
              </span>
              <span className="text-gray-800 dark:text-gray-200 font-medium">{choice.label}</span>
            </button>
          );
        })}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-8">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-[#2813e8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {currentAnswer !== undefined && (
          <button
            type="button"
            onClick={() => {
              if (step < QUESTIONS.length - 1) setStep((s) => s + 1);
              else setPhase('result');
            }}
            className="inline-flex items-center gap-2 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            {step < QUESTIONS.length - 1 ? 'Next' : 'See my risk score'}
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
